import { DisaggregatedMetric, GeoDetails, GeoTag, MetricMetadata } from "../services/types";

import printWhiteSvg from '../assets/print-white.svg';
import lakewoodImg from '../assets/lakewood/logo-lakewood.png';
import equitySvg from '../assets/Equity.svg';
import accessibilitySvg from '../assets/Accessibility.svg';
import livabilitySvg from '../assets/Livability.svg';
import economySvg from '../assets/economy.svg';
import environmentSvg from '../assets/Environment.svg';
import educationSvg from '../assets/Education.svg';

import PDFMetricCard from "../components/pdf/PDFMetricCard";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { useContext, useEffect, useState } from "react";
import { categorizeData, getIndexMetricDataPDF, getSortedMetrics, getSvgrMap, orgId } from "../utils/dashboardUtils";
import LoadingIndicator from "../components/LoadingIndicator";
import TitleBanner from "../components/pdf/TitleBanner";
import { indicatorsEnv } from "../utils/environment";
import RaceEthnicityBarChartCard from "../components/charts/RaceEthnicityBarChartCard";
import MetricBarChart from "../components/charts/MetricBarChart";
import AgeByGenderChart from "../components/charts/AgeByGenderChart";
import RoadQualityCharts from "../components/charts/gauge/RoadQualityCharts";
import CrimeCategoryChart from "../components/charts/CrimeCategoryChart";


interface MetricsModalProps {
    isOpen: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    geoDetails: GeoDetails[];
    metadata: MetricMetadata[] | null;
    geoTag: GeoTag | null;
    pdfUrl: string;
    logoUrl: string;
    legendUrl: string;
    selectedArea: string;
    siteUrl: string;
    selectionActive: boolean;
}

const MetricsModal: React.FC<MetricsModalProps> = ({ isOpen, onClose, metadata, geoDetails, geoTag, pdfUrl, logoUrl, legendUrl, siteUrl, selectedArea, selectionActive }) => {
    const { metricCategories } = useContext(MetricCategoriesContext);
    const [loading, setLoading] = useState(true);
    const [disaggregatedData, setDisaggregatedData] = useState<DisaggregatedMetric[]>([]);
    const geoEncoding = 'delta';
    const indicatorsUrl = `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getallindicatorsv2`;

    const fetchData = async () => {
        if (!isOpen || !orgId || metricCategories.length === 0) return;
    
        try {
            setLoading(true);
    
            if (orgId !== null) {
                // Generate an array of fetch promises, one for each category
                const fetchPromises = metricCategories.map(category => 
                    fetch(indicatorsUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            geoDetails,
                            orgId,
                            dashboardView: category,
                            geoEncoding,
                            geoTag,
                            allowDefault: true,
                            environment: indicatorsEnv
                        }),
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for ${category}. Response: ${response}`);
                        }
                        return response.json();
                    })
                );
    
                // Resolve all promises in parallel
                const results = await Promise.all(fetchPromises);
                console.log('results from category calls:', results);
    
                // Combine and categorize the results
                const combinedData = results.flat();
                setDisaggregatedData(combinedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            // setError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && metricCategories.length > 0) {
            console.log('Effect triggered:', { isOpen, geoDetails, orgId, metricCategories });
            fetchData();
        }
    }, [isOpen, geoDetails, orgId, metricCategories]);

    console.log('disaggreagted data:', disaggregatedData);
    
    // Function to categorize the data based on category
    // const categorizeData = (data: DisaggregatedMetric[]) => {
    //     return metricCategories.map(category => {
    //         // Filter metrics by category
    //         const filteredMetrics = data.filter(item => item.category === category);
    //         return {
    //             category,
    //             metrics: filteredMetrics
    //         };
    //     });
    // };

    // Categorize the disaggregated data
    const categorizedData = categorizeData(disaggregatedData);

    // Get sorted metrics for each dashboard panel
    const sortedDemographicMetrics = getSortedMetrics('Overview', categorizedData);
    const sortedAccessibilityMetrics = getSortedMetrics('Accessibility', categorizedData);
    const sortedLivabilityMetrics = getSortedMetrics('Livability', categorizedData);
    const sortedEducationMetrics = getSortedMetrics('Education', categorizedData);
    const sortedEconomyMetrics = getSortedMetrics('Economy', categorizedData);
    const sortedEnvironmentMetrics = getSortedMetrics('Environment', categorizedData);

    // Get index metric data for each dashboard panel
    const indexMetricDataDemographic = getIndexMetricDataPDF('Overview', metadata, disaggregatedData);
    const indexMetricDataAccessibility = getIndexMetricDataPDF('Accessibility', metadata, disaggregatedData);
    const indexMetricDataLivability = getIndexMetricDataPDF('Livability', metadata, disaggregatedData);
    const indexMetricDataEducation = getIndexMetricDataPDF('Education', metadata, disaggregatedData);
    const indexMetricDataEconomy = getIndexMetricDataPDF('Economy', metadata, disaggregatedData);
    const indexMetricDataEnvironment = getIndexMetricDataPDF('Environment', metadata, disaggregatedData);

    // Define metrics for order
    // Demographic
    const demoOne: DisaggregatedMetric[] = sortedDemographicMetrics[0];
    const demoTwo: DisaggregatedMetric[] = sortedDemographicMetrics[1];
    const immigration = disaggregatedData.find(i => i.id === 1291);
    const race = disaggregatedData.find(i => i.id === 1293);
    const female = disaggregatedData.find(i => i.id == 1289);
    const male = disaggregatedData.find(i => i.id == 1290);

    // Accessibility
    const a: DisaggregatedMetric[] = sortedAccessibilityMetrics[0];
    const a2: DisaggregatedMetric[] = sortedAccessibilityMetrics[1];
    const a3: DisaggregatedMetric[] = sortedAccessibilityMetrics[2];
    const pci = disaggregatedData.find(i => i.id == 44);

    // Livability
    const l: DisaggregatedMetric[] = sortedLivabilityMetrics[0];
    const crimeIndex = disaggregatedData.find(i => i.id === 56);
    const personal = disaggregatedData.find(i => i.id == 58);
    const property = disaggregatedData.find(i => i.id == 59);
    const society = disaggregatedData.find(i => i.id == 60);

    // Education
    const edu: DisaggregatedMetric[] = sortedEducationMetrics[0];

    // Economy
    const e: DisaggregatedMetric[] = sortedEconomyMetrics[0];

    // Environment
    const env: DisaggregatedMetric[] = sortedEnvironmentMetrics[0];

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = "DashboardMetrics";  // Set your desired title here
        window.print();
        document.title = originalTitle;  // Restore the original title
    };

    if (loading) {
        return <LoadingIndicator/>
    }

    return (
        <div id="print-container" className="fixed inset-0 bg-white p-4 overflow-y-auto z-50">
            {/* Wrapper to limit the modal's width */}
            <div className="max-w-[800px] mx-auto w-full mb-8">
                <div className="relative h-full w-full">
                    {/* Print and Close buttons */}
                    <div className="flex justify-center items-center no-print mb-[1rem]">
                        <button 
                            onClick={handlePrint}
                            className="flex mx-2 bg-[#0A5F75] border border-[#0A5F75] text-white py-1 pl-7 pr-8 rounded-md"
                        >
                            <img src={printWhiteSvg} alt="Print" className="w-3 h-3 mr-2 mt-[0.5px]" />
                            <p className="text-xs font-bold">Print</p>
                        </button>
                        <button 
                            onClick={onClose}
                            className="bg-transparent border border-[#0A5F75] py-1 px-8 rounded-md hover:bg-color-cai-lightGray"
                        >
                            <p className="text-xs text-[#0A5F75] font-bold">Close</p>
                        </button>
                    </div>

                    {/* Page 1 */}
                    <div className="flex flex-col page">
                        <div className="flex flex-col items-center justify-center relative mb-4">
                            <div className="w-full h-auto p-2 flex justify-between items-center bg-[#1A3A67] rounded-md">
                                <img src={lakewoodImg} alt='City of Lakewood Logo' className="w-20 h-20"/>
                                <div className="flex flex-col items-start justify-start mx-4 w-full">
                                    <p className="text-white font-bold text-[18px]">City of Lakewood, WA</p>
                                    <p className="text-xs text-white">{selectedArea || 'Lakewood, WA'}</p>
                                    <p className="text-xs text-white font-bold">{siteUrl || ''}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-grow">
                            <div className="relative inline-block">
                                {pdfUrl && <img src={pdfUrl} className="h-auto w-auto" alt="Esri Map Image" /> }
                                {/* Removing legend for now due to performance concerns */}
                                { <div className="flex justify-between">
                                    { legendUrl && <img src={legendUrl} className="h-[188px] w-[220px] ml-6 mt-[-5rem]" alt="Esri Legend Image" /> }
                                    { logoUrl && <img src={logoUrl} className="absolute bottom-4 right-2 h-10 w-30" alt="Esri Logo Image" /> }
                                </div> }
    
                            </div>
                        </div>
                        {/* Demographic Metrics */}
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md mb-4">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Demographics"/>
                                <p className="text-port-text mx-6 text-xs mt-2">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-4 gap-4 my-2 mx-4 pb-2">
                                    {demoOne.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}

                                    {demoTwo.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* <Footer/> */}
                    </div>
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-4 pb-4">
                                <TitleBanner metric={indexMetricDataDemographic} geoDetails={geoDetails} geoTag={geoTag} svg={equitySvg} text="Demographics"/>
                                <p className="text-port-text mx-6 text-xs mt-4">Indicators shown below depict demographic details for the population within the selected area.</p>
                                <div className="grid grid-cols-2 gap-4 m-4">
                                    <div className="col-span-1 w-full">
                                        <RaceEthnicityBarChartCard
                                            raceMetric={race}
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            height="h-56"
                                            background="bg-white"
                                            barColor="#1a3a67"
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                    <div className="col-span-1 w-full">
                                        <MetricBarChart 
                                            metric={immigration} 
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            height="h-40" 
                                            background="bg-white"
                                            barColor="#1a3a67"
                                            topFive={true}
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                    <div className="col-span-2 w-full">
                                        <AgeByGenderChart
                                            maleMetric={male}
                                            femaleMetric={female}
                                            metadataMetricId={1294}
                                            title="Age & Gender"
                                            subtitle="Population by age group"
                                            maleColor="#1a3a67"
                                            femaleColor="#9d1d1f"
                                            height="h-60"
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Accessibility*/}
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-4 pb-4">
                                <TitleBanner metric={indexMetricDataAccessibility} geoDetails={geoDetails} geoTag={geoTag} svg={accessibilitySvg} text="Accessiblity"/>

                                <div className="grid grid-cols-2 gap-4 m-4">
                                    <h2 className="text-sm font-semibold text-port-text col-span-2">Mobility & Access</h2>

                                    {a.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}

                                    <div className="col-span-1 w-full">
                                        <RoadQualityCharts
                                            metric={pci}
                                            disaggData={disaggregatedData}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Accessibility*/}
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-4 pb-4">
                                <TitleBanner metric={indexMetricDataAccessibility} geoDetails={geoDetails} geoTag={geoTag} svg={accessibilitySvg} text="Accessiblity"/>
                                <div className="grid grid-cols-4 gap-4 m-4">
                                    <h2 className="text-sm font-semibold text-port-text col-span-4">Civic & Digital Access</h2>
                                    {a2.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>

                                <div className="grid grid-cols-3 gap-4 m-4">
                                    <h2 className="text-sm font-semibold text-port-text col-span-3">Parks & Community</h2>

                                    {a3.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Page 2: Livability*/}
                    <div className="page">
                        <div className="content bg-[#f8f8fa]">
                            <div className="shadow-md rounded-md my-8 pb-4">
                                <TitleBanner metric={indexMetricDataLivability} geoDetails={geoDetails} geoTag={geoTag} svg={livabilitySvg} text="Livability"/>
                                <div className="grid grid-cols-3 gap-4 m-4">
                                    {l.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                    <div className="col-span-3 w-full">
                                        <CrimeCategoryChart
                                            crimeIndexMetric={crimeIndex}
                                            propertyMetric={property}
                                            personalMetric={personal}
                                            societyMetric={society}
                                            subtitle="Total from January 1, 2025 to December 31, 2025"
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive
                                            disaggData={disaggregatedData}
                                            height="h-28"
                                            showMetadataIcon={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <Footer /> */}
                    </div>

                    {/* Education*/}
                    <div className="page">
                        <div className="content bg-[#f8f8fa]">
                            <div className="shadow-md rounded-md my-8 pb-4">
                                <TitleBanner metric={indexMetricDataEducation} geoDetails={geoDetails} geoTag={geoTag} svg={educationSvg} text="Education"/>
                                <div className="grid grid-cols-3 gap-4 m-4">
                                    {edu.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        {/* <Footer /> */}
                    </div>

                    {/* Page 3: Economy */}
                    <div className="page">
                        <div className="content">
                            <div className="bg-[#f8f8fa] shadow-md rounded-md my-8 pb-4">
                                <TitleBanner metric={indexMetricDataEconomy} geoDetails={geoDetails} geoTag={geoTag} svg={economySvg} text="Economy"/>
                                <div className="grid grid-cols-3 gap-4 m-4">
                                    {e.map(metric => (
                                        <PDFMetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={getSvgrMap(metric.id)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Page 4: Environment */}
                    <div className="page">
                        <div className="bg-[#f8f8fa] shadow-md rounded-md my-8 pb-4">
                            <TitleBanner metric={indexMetricDataEnvironment} geoDetails={geoDetails} geoTag={geoTag} svg={environmentSvg} text="Environment"/>
                            <div className="grid grid-cols-3 gap-4 m-4">
                                {env.map(metric => (
                                    <PDFMetricCard
                                        key={metric.id}
                                        metric={metric}
                                        width="w-full"
                                        svg={getSvgrMap(metric.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetricsModal;